import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user: currentUser } = useAuth();
  
  // For user creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: 'maker',
    is_active: true
  });
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${apiUrl}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      setCreateError(null);
      setCreateSuccess(null);
      
      const token = localStorage.getItem('token');
      
      await axios.post(`${apiUrl}/auth/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCreateSuccess('User created successfully');
      setNewUser({
        username: '',
        email: '',
        password: '',
        role: 'maker',
        is_active: true
      });
      
      // Refresh the user list
      fetchUsers();
      
      // Close the modal after a short delay
      setTimeout(() => {
        setShowCreateModal(false);
      }, 1500);
    } catch (err) {
      console.error('Error creating user:', err);
      setCreateError(err.response?.data?.detail || 'Failed to create user');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser({
      ...newUser,
      [name]: value
    });
  };
  
  const handleRoleChange = (e) => {
    setNewUser({
      ...newUser,
      role: e.target.value
    });
  };
  
  return (
    <Container>
      <h2 className="mb-4">User Management</h2>
      
      {error && (
        <Alert variant="danger">{error}</Alert>
      )}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Users</h5>
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowCreateModal(true)}
          >
            Create New User
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">Loading users...</div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No users found</td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.username}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`badge bg-${getRoleBadgeColor(user.role)}`}>
                          {formatRole(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge bg-${user.is_active ? 'success' : 'secondary'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <Button 
                          variant="outline-secondary" 
                          size="sm" 
                          className="me-2"
                          disabled={user.id === currentUser.id}
                        >
                          Edit
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          disabled={user.id === currentUser.id}
                        >
                          Deactivate
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      
      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && (
            <Alert variant="danger">{createError}</Alert>
          )}
          
          {createSuccess && (
            <Alert variant="success">{createSuccess}</Alert>
          )}
          
          <Form onSubmit={handleCreateUser}>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={newUser.username}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={newUser.email}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={newUser.password}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                name="role"
                value={newUser.role}
                onChange={handleRoleChange}
                required
              >
                <option value="maker">Data Uploader</option>
                <option value="campaign_manager">Campaign Manager</option>
                <option value="cibil_uploader">CIBIL Uploader</option>
                <option value="admin">Administrator</option>
              </Form.Select>
            </Form.Group>
            
            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create User
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

// Helper function to format role names
const formatRole = (role) => {
  const roleMap = {
    'admin': 'Administrator',
    'maker': 'Data Uploader',
    'campaign_manager': 'Campaign Manager'
  };
  return roleMap[role] || role;
};

// Helper function to get badge color based on role
const getRoleBadgeColor = (role) => {
  const colorMap = {
    'admin': 'danger',
    'maker': 'primary',
    'campaign_manager': 'success'
  };
  return colorMap[role] || 'secondary';
};

export default UserManagementPage;